import { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IPairing, IPosition, IPositionOverride, IFullPairingPosition } from "@vmosolutions/oc-interfaces";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { TableCell, TableRow, Grid, Divider, Button } from "@mui/material";
import CallMissedIcon from "@mui/icons-material/CallMissed";
import _ from "lodash";
import { updatePairingEditor } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";

interface IFlightPositionOverrideProps {
  id: string;
  flightId: string;
  active?: boolean;
}

/** Anaylize any position overrides applied to pairing and clean out any that aren't valid or redundant (match default positions) */
export const validatePositionOverrides = (formValues: IPairing): IPairing => {
  const defaultPositions = formValues.positions.map((pos) => ({
    crewId: pos?.crew?._id,
    positionId: pos?.position?._id,
  }));
  const newOverrides = formValues.positionOverrides?.filter((override) => {
    const flightOverride = override.positions;
    let changeCount = 0;
    if (!override.positions || override.positions.length === 0) {
      return false;
    } else {
      for (let i = 0; i < defaultPositions.length; i++) {
        const defaultPosition = defaultPositions[i];
        const flightOverridePosition = flightOverride.find((pos) => pos.crewId === defaultPosition.crewId);
        if (flightOverridePosition && defaultPosition.positionId !== flightOverridePosition.positionId) {
          changeCount = changeCount + 1;
        }
      }
    }
    // Only return overrides that contain changes.
    return changeCount > 0;
  });
  return { ...formValues, positionOverrides: newOverrides };
};

/**
 * FLIGHT POSITION OVERRIDES
 * @param props -
 */
export const FlightPositionOverride = (props: IFlightPositionOverrideProps): JSX.Element => {
  const { id, flightId, active } = props;
  const dispatch = useDispatch();
  const positionOptions = useSelector((state: IRootState) => state.structures.customerStructure.positions);
  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[props.id]);

  const positionOverride = storedPairing.positionOverrides?.find((override) => override.flightId === flightId);
  const positions = positionOverride?.positions ?? null;
  const defaultPairingPositions: IFullPairingPosition[] = storedPairing?.positions ?? [];

  const handleRemoveOverride = useCallback(() => {
    const updatePositionOverrides: IPositionOverride[] = storedPairing?.positionOverrides
      ? _.cloneDeep(storedPairing.positionOverrides)
      : [];
    const index = updatePositionOverrides.findIndex((ov) => ov.flightId === flightId);
    if (index > -1) {
      updatePositionOverrides.splice(index, 1);
      // Delete existing override
      dispatch(updatePairingEditor(id, { positionOverrides: updatePositionOverrides }));
    }
  }, [storedPairing, flightId]);

  const handleChangeOverridePosition = useCallback(
    (crewId: string, positionId: string): void => {
      // Deep clone is required here otherwise position assignments on existed overrides are mutating object.
      const updatePositionOverrides: IPositionOverride[] = storedPairing?.positionOverrides
        ? _.cloneDeep(storedPairing.positionOverrides)
        : [];
      const updatePositionOverride = updatePositionOverrides?.find((override) => override.flightId === flightId);
      const updatePositions = updatePositionOverride?.positions ?? [];
      const crewPositionIndex = updatePositions.findIndex((up) => up.crewId === crewId);
      if (crewPositionIndex > -1) {
        updatePositions[crewPositionIndex].positionId = positionId;
      } else {
        updatePositions.push({ crewId, positionId });
      }
      // Add or modify existing override.
      dispatch(updatePairingEditor(id, { positionOverrides: updatePositionOverrides }));
    },
    [storedPairing, flightId]
  );

  return (
    <TableRow className={"table_row position_overrides"} selected={active}>
      <TableCell colSpan={5}>
        {Array.isArray(positions) && (
          <Grid className="position_override_control" container>
            <Grid item className="position_override_arrow">
              <CallMissedIcon className="icon" />
              Position Override
            </Grid>

            <Grid item style={{ flexGrow: 1 }}>
              <Grid container justifyContent="flex-start">
                {defaultPairingPositions.map((fullPos: IFullPairingPosition, index) => {
                  const { crew, position } = fullPos;

                  //!! Added to make sure that users can add new positions that may not be initially occupied.
                  if (!crew) {
                    return (
                      <Grid item style={{ paddingLeft: 20 }} key={index}>
                        <Grid container className="crew_position_display" alignItems="flex-start" wrap="nowrap">
                          <PositionBadge position={position} filled={false} />
                          <Grid item style={{ marginLeft: 5 }}>
                            Unassigned
                          </Grid>
                        </Grid>
                      </Grid>
                    );
                  }

                  const crewName = `${crew?.firstName} ${crew?.lastName}`;
                  const crewPositionOptions = crew?.crewInfo?.positions?.sort(
                    (a, b) => a.displayOrder - b.displayOrder
                  );

                  // Display selected override position. If not available show default.
                  const assignedPosId = positions.find((p) => p.crewId === crew._id)?.positionId;
                  const posValue = positionOptions.find((o) => o._id === assignedPosId) ?? position;

                  return (
                    <Grid item style={{ paddingLeft: 20 }} key={index}>
                      {!crew ? null : active ? (
                        <SelectField
                          className="selectfield position_override"
                          name={`flight-${flightId}_PositionOverride-crew${crew._id}`}
                          label={crewName}
                          value={posValue}
                          onChange={(option) => handleChangeOverridePosition(crew._id, option._id)}
                          options={crewPositionOptions}
                          getOptionLabel={(option) => option.description}
                          menuPlacement="top"
                          formatOptionLabel={(option: any) => <PositionBadge position={option} filled={true} />}
                          getOptionValue={(option: IPosition) => option._id}
                          placeholder="Select Position"
                          hideSelectedOptions={true}
                        />
                      ) : (
                        <Grid container className="crew_position_display" alignItems="flex-start" wrap="nowrap">
                          <PositionBadge position={posValue} filled={true} isInactive={!crew?.crewInfo?.isActive} />
                          <Grid item style={{ marginLeft: 5 }}>{`${crewName}`}</Grid>
                        </Grid>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            {active && (
              <Grid item>
                <Button className="remove_override_btn" onClick={handleRemoveOverride}>
                  Remove Override
                </Button>
              </Grid>
            )}
          </Grid>
        )}

        <Divider light={true} />
      </TableCell>
    </TableRow>
  );
};
